import * as Sentry from "@sentry/react";
import { apiFetch } from "@common/lib/api";
import type { ClinicResult } from "./setup-flow-config";

/**
 * Search for veterinary clinics by name via Google Places API (backend proxy).
 * Falls back to mock data in development if the API call fails.
 * Throws on production errors so the caller can handle them.
 */
export async function searchClinics(clinicName: string): Promise<ClinicResult[]> {
    try {
        const results = await apiFetch<ClinicResult[]>("onboarding/clinic-lookup", {
            method: "POST",
            json: { clinicName },
        });
        return results || [];
    } catch (error) {
        // Fall back to mock in development
        if (import.meta.env.DEV) {
            return mockClinicSearch(clinicName);
        }
        Sentry.captureException(error);
        throw error;
    }
}

// --- Mock implementation for development ---

async function mockClinicSearch(clinicName: string): Promise<ClinicResult[]> {
    // Simulate network delay
    await new Promise((r) => setTimeout(r, 800));

    const lower = clinicName.toLowerCase();

    // "valley" → 2 results (triggers pick step)
    if (lower.includes("valley")) {
        return [
            {
                name: "Valley Veterinary Clinic",
                formattedAddress: "1234 Peachtree Rd NE, Atlanta, GA 30309",
                street: "1234 Peachtree Rd NE",
                city: "Atlanta",
                state: "GA",
                zip: "30309",
                country: "US",
                phone: "(404) 555-0123",
                website: "valleyvetatlanta.com",
            },
            {
                name: "Valley Animal Hospital",
                formattedAddress: "5678 Roswell Rd, Sandy Springs, GA 30342",
                street: "5678 Roswell Rd",
                city: "Sandy Springs",
                state: "GA",
                zip: "30342",
                country: "US",
                phone: "(404) 555-0456",
                website: null,
            },
        ];
    }

    // > 3 chars → 1 result (triggers confirm step)
    if (clinicName.length > 3) {
        return [
            {
                name: clinicName,
                formattedAddress: "100 Main St, Anytown, CA 90210",
                street: "100 Main St",
                city: "Anytown",
                state: "CA",
                zip: "90210",
                country: "US",
                phone: "(555) 123-4567",
                website: null,
            },
        ];
    }

    // ≤ 3 chars → 0 results (triggers manual entry)
    return [];
}
